import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Row, Col, Space, Tag, Image, Spin, message } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlayCircleOutlined, 
  TeamOutlined, 
  ClockCircleOutlined, 
  TrophyOutlined,
  CloudServerOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import api from '../../services/api';
import PublicHeader from '../../components/Layout/PublicHeader';
import './GameIntro.css';

const { Title, Paragraph, Text } = Typography;

interface GameType {
  id: number;
  name: string;
  display_name: string;
  description: string;
  icon?: string;
  supports_mods?: boolean;
  is_steam_game?: boolean;
  background_image_url?: string;
}

interface GameIntroData {
  id: number;
  name: string;
  display_name: string;
  description: string;
  icon?: string;
  supports_mods?: boolean;
  is_steam_game?: boolean;
  background_image_url?: string;
  // 游戏介绍数据
  hero_image?: string;
  screenshots?: string[];
  features?: string[];
  system_requirements?: {
    minimum?: string;
    recommended?: string;
  };
  genres?: string[];
  release_date?: string;
  developer?: string;
  publisher?: string;
  rating?: number;
  player_count?: string;
  game_modes?: string[];
}

const GameIntro: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [gameData, setGameData] = useState<GameIntroData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (gameId) {
      loadGameData(parseInt(gameId));
    }
  }, [gameId]);

  const loadGameData = async (id: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/games/types`);
      const games = response.data;
      const game = games.find((g: GameType) => g.id === id);
      
      if (game) {
        // 模拟获取详细的游戏介绍数据
        const gameIntroData: GameIntroData = {
          ...game,
          hero_image: game.icon || '/images/game-heroes/placeholder.jpg',
          screenshots: [
            game.icon || '/images/game-screenshots/placeholder1.jpg',
            game.icon || '/images/game-screenshots/placeholder2.jpg',
            game.icon || '/images/game-screenshots/placeholder3.jpg',
          ],
          features: [
            '多人合作游戏',
            '开放世界探索',
            '建造与生存',
            'MOD支持',
            '跨平台游戏'
          ],
          system_requirements: {
            minimum: 'Windows 10, Intel i5, 8GB RAM, GTX 1060',
            recommended: 'Windows 11, Intel i7, 16GB RAM, RTX 3070'
          },
          genres: ['生存', '建造', '多人', '沙盒'],
          release_date: '2021年',
          developer: '游戏工作室',
          publisher: '发行商',
          rating: 4.5,
          player_count: '10,000+ ',
          game_modes: ['单人', '多人合作', 'PvP']
        };
        setGameData(gameIntroData);
      } else {
        message.error('游戏不存在');
        navigate('/');
      }
    } catch (error) {
      console.error('加载游戏数据失败:', error);
      message.error('加载游戏数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateServer = () => {
    navigate(`/servers/create?game=${gameId}`);
  };

  if (loading) {
    return (
      <div 
        className="game-intro-page"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          minHeight: '100vh'
        }}
      >
        <PublicHeader currentPage="game-intro" />
        <div className="game-background-overlay"></div>
        <div className="game-intro-content">
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: '20px', color: 'white' }}>加载游戏信息中...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!gameData) {
    return (
      <div 
        className="game-intro-page"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          minHeight: '100vh'
        }}
      >
        <PublicHeader currentPage="game-intro" />
        <div className="game-background-overlay"></div>
        <div className="game-intro-content">
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Title level={2} style={{ color: 'white' }}>游戏不存在</Title>
            <Button type="primary" onClick={() => navigate('/')}>
              返回首页
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="game-intro-page"
      style={{
        backgroundImage: gameData.background_image_url 
          ? `url(${gameData.background_image_url})` 
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '100vh'
      }}
    >
      <PublicHeader currentPage="game-intro" />
      
      {/* 背景遮罩层 */}
      <div className="game-background-overlay"></div>

      <div className="game-intro-content">
        {/* 返回按钮 */}
        <div className="back-button-container">
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            className="back-button"
          >
            返回首页
          </Button>
        </div>

         {/* 游戏英雄区域 */}
         <div className="game-hero-section">
           <Row gutter={[32, 32]} align="middle">
             <Col xs={24} lg={12}>
               <div className="game-hero-info">
                 <div className="game-icon-container">
                   <Image
                     src={gameData.icon || '/images/game-icons/placeholder_184x69.jpg'}
                     alt={gameData.display_name}
                     width={120}
                     height={45}
                     style={{ objectFit: 'cover', borderRadius: '8px' }}
                     fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjQ1IiB2aWV3Qm94PSIwIDAgMTIwIDQ1IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjQ1IiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjYwIiB5PSIyMiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+R2FtZTwvdGV4dD4KPC9zdmc+"
                     preview={false}
                   />
                 </div>
                 <Title level={1} className="game-title">
                   {gameData.display_name}
                 </Title>
                 <Paragraph className="game-description">
                   {gameData.description || '一款精彩的多人游戏，支持建造、生存和探索。与朋友一起体验无限可能！'}
                 </Paragraph>
                 
                 <div className="game-tags">
                   <Space wrap>
                     {gameData.genres?.map((genre, index) => (
                       <Tag key={index}>{genre}</Tag>
                     ))}
                     {gameData.is_steam_game && <Tag>Steam</Tag>}
                     {gameData.supports_mods && <Tag>MOD支持</Tag>}
                   </Space>
                 </div>

                 <div className="game-stats">
                   <Row gutter={[16, 16]}>
                     <Col span={8}>
                       <div className="stat-item">
                         <TeamOutlined className="stat-icon" />
                         <div>
                           <div className="stat-value">{gameData.player_count}</div>
                           <div className="stat-label">在线玩家</div>
                         </div>
                       </div>
                     </Col>
                     <Col span={8}>
                       <div className="stat-item">
                         <TrophyOutlined className="stat-icon" />
                         <div>
                           <div className="stat-value">{gameData.rating}/5.0</div>
                           <div className="stat-label">游戏评分</div>
                         </div>
                       </div>
                     </Col>
                     <Col span={8}>
                       <div className="stat-item">
                         <ClockCircleOutlined className="stat-icon" />
                         <div>
                           <div className="stat-value">{gameData.release_date}</div>
                           <div className="stat-label">发布时间</div>
                         </div>
                       </div>
                     </Col>
                   </Row>
                 </div>
               </div>
             </Col>
             <Col xs={24} lg={12}>
               {/* 开通服务器卡片 */}
               <Card className="create-server-card">
                 <div className="create-server-content">
                   <div className="create-server-info">
                     <CloudServerOutlined className="server-icon" />
                     <div>
                       <Title level={3} style={{ margin: 0, color: 'white' }}>
                         准备开始游戏？
                       </Title>
                       <Paragraph style={{ margin: '8px 0 0 0', color: 'rgba(255, 255, 255, 0.8)' }}>
                         创建您的专属游戏服务器，与朋友一起享受游戏乐趣
                       </Paragraph>
                     </div>
                   </div>
                   <Button 
                     type="primary" 
                     size="large"
                     icon={<PlayCircleOutlined />}
                     onClick={handleCreateServer}
                     className="create-server-button"
                   >
                     立即开通服务器
                   </Button>
                 </div>
               </Card>
             </Col>
           </Row>
         </div>

        {/* 游戏特色 */}
        <div className="game-features-section">
          <Card className="game-features-card">
            <Title level={2} className="section-title">游戏特色</Title>
            <Row gutter={[16, 16]}>
              {gameData.features?.map((feature, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <div className="feature-item">
                    <div className="feature-icon">✨</div>
                    <div className="feature-text">{feature}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </div>

        {/* 游戏截图 */}
        <div className="game-screenshots-section">
          <Card className="game-screenshots-card">
            <Title level={2} className="section-title">游戏截图</Title>
            <Row gutter={[16, 16]}>
              {gameData.screenshots?.map((screenshot, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <div className="screenshot-item">
                    <Image
                      src={screenshot}
                      alt={`游戏截图 ${index + 1}`}
                      style={{ 
                        width: '100%', 
                        height: '200px', 
                        objectFit: 'cover', 
                        borderRadius: '12px' 
                      }}
                      fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjE1MCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5TY3JlZW5zaG90PC90ZXh0Pgo8L3N2Zz4="
                      preview={{
                        mask: <div style={{ color: 'white' }}>预览</div>
                      }}
                    />
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </div>

        {/* 系统要求 */}
        <div className="game-requirements-section">
          <Card className="game-requirements-card">
            <Title level={2} className="section-title">系统要求</Title>
            <Row gutter={[32, 32]}>
              <Col xs={24} md={12}>
                <div className="requirements-item">
                  <Title level={4} style={{ color: '#ff6b6b' }}>最低配置</Title>
                  <Text>{gameData.system_requirements?.minimum}</Text>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className="requirements-item">
                  <Title level={4} style={{ color: '#51cf66' }}>推荐配置</Title>
                  <Text>{gameData.system_requirements?.recommended}</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GameIntro;
